import { Controller, useForm } from 'react-hook-form';
import { Loader2, X } from 'lucide-react';
import { useCreateSharedLinkMutation, useReportCampaignsQuery } from '../api/useReportsQueries';
import { CampaignMultiSelect } from './CampaignMultiSelect';
import { getCampaignOptionId, normalizeCampaignOptions, THEME_COLOR_OPTIONS } from '../utils/reportsUtils';
import type { CreateSharedLinkPayload, ReportExpiryDays, ReportPermission } from '../types/reports';

type CreateLinkFormValues = {
  campaignIds: string[];
  selectAll: boolean;
  expiryDays: ReportExpiryDays;
  permission: ReportPermission;
  themeColor: string;
  note: string;
};

type CreateLinkModalProps = {
  open: boolean;
  onClose: () => void;
};

const selectCls =
  'w-full appearance-none rounded-xl border border-[var(--dash-line)] bg-[var(--dash-soft)] px-3.5 py-3 text-sm text-[var(--dash-ink)] outline-none transition focus:border-[var(--dash-brand)] focus:bg-white focus:ring-2 focus:ring-[var(--dash-brand)]/15';

export function CreateLinkModal({ open, onClose }: CreateLinkModalProps) {
  const campaignsQ = useReportCampaignsQuery();
  const createMut = useCreateSharedLinkMutation();
  const campaignOptions = normalizeCampaignOptions(campaignsQ.data);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { errors },
  } = useForm<CreateLinkFormValues>({
    defaultValues: {
      campaignIds: [],
      selectAll: false,
      expiryDays: 30,
      permission: 'ALLOW_COMMENTS',
      themeColor: 'BLUE',
      note: '',
    },
  });

  const selectAll = watch('selectAll');

  if (!open) return null;

  const close = () => {
    reset();
    onClose();
  };

  const onSubmit = (values: CreateLinkFormValues) => {
    const payload: CreateSharedLinkPayload = {
      ...values,
      campaignIds: values.selectAll
        ? campaignOptions.map((opt) => getCampaignOptionId(opt))
        : values.campaignIds,
      expiryDays: Number(values.expiryDays) as ReportExpiryDays,
    };
    createMut.mutate(payload, { onSuccess: close });
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <button type="button" className="absolute inset-0" aria-label="Close" onClick={close} />

      <form
        onSubmit={handleSubmit(onSubmit)}
        onClick={(e) => e.stopPropagation()}
        className="camp-fade-up relative z-10 flex max-h-[90vh] w-full max-w-2xl flex-col overflow-y-auto rounded-3xl bg-white shadow-2xl"
      >
        <div className="sticky top-0 z-20 flex items-center justify-between border-b border-[var(--dash-line)] bg-white px-6 py-4">
          <div className="text-left">
            <h2 className="m-0 text-xl font-bold text-[var(--dash-ink)]">Create Campaign Link</h2>
            <p className="mt-1 mb-0 text-sm text-[var(--dash-muted)]">
              Share your dashboard with external viewers
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            className="rounded-full p-2.5 text-[var(--dash-muted)] transition hover:bg-[var(--dash-soft)] hover:text-[var(--dash-ink)]"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-col gap-4 px-6 pt-4 pb-6">
          <div
            role="checkbox"
            aria-checked={selectAll}
            tabIndex={0}
            onClick={() => setValue('selectAll', !selectAll)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setValue('selectAll', !selectAll);
              }
            }}
            className="flex cursor-pointer items-center gap-3 rounded-xl border border-[var(--dash-line)] bg-[var(--dash-soft)]/50 p-3 transition hover:border-[var(--dash-brand)]"
          >
            <span
              className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-lg border-2 transition ${
                selectAll
                  ? 'border-[var(--dash-brand)] bg-[var(--dash-brand)]'
                  : 'border-[var(--dash-line)] bg-white'
              }`}
            >
              {selectAll ? (
                <svg className="h-3 w-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                </svg>
              ) : null}
            </span>
            <span className="flex flex-col text-left">
              <span className="text-sm font-semibold text-[var(--dash-ink)]">
                Share All Campaigns
              </span>
              <span className="text-[11px] font-normal text-[var(--dash-muted)] italic">
                Selecting this will share access to every campaign in your dashboard.
              </span>
            </span>
          </div>

          <div className={`flex items-center gap-4 transition-opacity ${selectAll ? 'opacity-30' : 'opacity-100'}`}>
            <div className="h-px flex-1 bg-[var(--dash-line)]" />
            <span className="text-[10px] font-bold tracking-widest text-[var(--dash-muted)] uppercase">OR</span>
            <div className="h-px flex-1 bg-[var(--dash-line)]" />
          </div>

          <Controller
            name="campaignIds"
            control={control}
            rules={{ validate: (v) => selectAll || v.length > 0 || 'Select at least one campaign' }}
            render={({ field: { value, onChange } }) => (
              <CampaignMultiSelect
                options={campaignOptions}
                value={value}
                onChange={onChange}
                disabled={selectAll}
                error={errors.campaignIds?.message}
              />
            )}
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5 text-left">
              <label className="text-[13px] font-semibold text-[var(--dash-ink)]">
                Expiry Days <span className="text-red-500">*</span>
              </label>
              <select {...register('expiryDays', { required: true })} className={selectCls}>
                <option value={7}>7 Days</option>
                <option value={15}>15 Days</option>
                <option value={30}>30 Days</option>
              </select>
            </div>

            <div className="flex flex-col gap-1.5 text-left">
              <label className="text-[13px] font-semibold text-[var(--dash-ink)]">
                Permission <span className="text-red-500">*</span>
              </label>
              <select {...register('permission', { required: true })} className={selectCls}>
                <option value="ALLOW_COMMENTS">Allow Comments</option>
                <option value="READ_ONLY">Read Only</option>
              </select>
            </div>
          </div>

          <div className="flex flex-col gap-1.5 text-left">
            <label className="text-[13px] font-semibold text-[var(--dash-ink)]">
              Theme Color <span className="text-red-500">*</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {THEME_COLOR_OPTIONS.map((opt) => {
                const active = watch('themeColor') === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setValue('themeColor', opt.value)}
                    className={`inline-flex items-center gap-2 rounded-xl border px-3.5 py-2.5 text-sm font-semibold transition ${
                      active
                        ? 'border-[var(--dash-brand)] bg-[var(--dash-brand-soft)] text-[var(--dash-brand)]'
                        : 'border-[var(--dash-line)] bg-[var(--dash-soft)] text-[var(--dash-ink)] hover:border-[var(--dash-brand)]'
                    }`}
                  >
                    <span className="h-3 w-3 rounded-full" style={{ backgroundColor: opt.swatch }} />
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col gap-1.5 text-left">
            <label className="text-[13px] font-semibold text-[var(--dash-ink)]">Note</label>
            <textarea
              {...register('note')}
              rows={3}
              placeholder="e.g. Hi John, here's your campaign dashboard."
              className="w-full resize-y rounded-xl border border-[var(--dash-line)] bg-[var(--dash-soft)] px-3.5 py-3 text-sm text-[var(--dash-ink)] outline-none transition focus:border-[var(--dash-brand)] focus:bg-white focus:ring-2 focus:ring-[var(--dash-brand)]/15"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-[var(--dash-line)] px-6 py-4">
          <button
            type="button"
            onClick={close}
            className="rounded-xl border border-[var(--dash-line)] bg-white px-5 py-2.5 text-sm font-semibold text-[var(--dash-ink)] transition hover:bg-[var(--dash-soft)]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createMut.isPending}
            className="dash-btn-gradient inline-flex min-w-[120px] items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white transition disabled:opacity-70"
          >
            {createMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Link'}
          </button>
        </div>
      </form>
    </div>
  );
}
